import { type FC, useMemo } from "react";

import type { UsageFilters } from "../../types/usage";

import { normalizeTextFilter } from "../../utils/dateFilters";
import { type FilterField, FiltersToolbar } from "../common/FiltersToolbar";

export interface UsageFiltersPanelProps {
  filters: Required<Pick<UsageFilters, "fromTimestampMs" | "toTimestampMs">> &
    Omit<UsageFilters, "fromTimestampMs" | "toTimestampMs">;
  isLoading: boolean;
  onSetFilters: (patch: UsageFilters) => void;
  onSetDateRange: (fromTimestampMs: number, toTimestampMs: number) => void;
  onReset: () => void;
  onRefresh: () => void;
}

export const UsageFiltersPanel: FC<UsageFiltersPanelProps> = ({
  filters,
  isLoading,
  onSetFilters,
  onSetDateRange,
  onReset,
  onRefresh,
}) => {
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.provider) {
      count++;
    }
    if (filters.model) {
      count++;
    }
    if (filters.stage) {
      count++;
    }
    if (filters.threadId) {
      count++;
    }
    if (filters.taskId) {
      count++;
    }
    if (filters.ticketId) {
      count++;
    }
    return count;
  }, [filters]);

  const fields: FilterField[] = useMemo(
    () => [
      {
        key: "provider",
        label: "Provider",
        onChange: (value) =>
          onSetFilters({ provider: normalizeTextFilter(value) }),
        placeholder: "openai",
        value: filters.provider ?? "",
      },
      {
        key: "model",
        label: "Model",
        onChange: (value) =>
          onSetFilters({ model: normalizeTextFilter(value) }),
        placeholder: "gpt-4o",
        value: filters.model ?? "",
      },
      {
        key: "stage",
        label: "Stage",
        onChange: (value) =>
          onSetFilters({ stage: normalizeTextFilter(value) }),
        placeholder: "llm",
        value: filters.stage ?? "",
      },
      {
        key: "threadId",
        label: "Thread",
        onChange: (value) =>
          onSetFilters({ threadId: normalizeTextFilter(value) }),
        placeholder: "thr_...",
        value: filters.threadId ?? "",
      },
      {
        key: "taskId",
        label: "Task",
        onChange: (value) =>
          onSetFilters({ taskId: normalizeTextFilter(value) }),
        placeholder: "task_...",
        value: filters.taskId ?? "",
      },
      {
        key: "ticketId",
        label: "Ticket",
        onChange: (value) =>
          onSetFilters({ ticketId: normalizeTextFilter(value) }),
        placeholder: "KAN-728",
        value: filters.ticketId ?? "",
      },
    ],
    [filters, onSetFilters],
  );

  return (
    <FiltersToolbar
      isLoading={isLoading}
      activeFilterCount={activeFilterCount}
      onRefresh={onRefresh}
      onReset={onReset}
      fromTimestampMs={filters.fromTimestampMs}
      toTimestampMs={filters.toTimestampMs}
      onSetDateRange={onSetDateRange}
      fields={fields}
    />
  );
};
